import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  FunctionField,
  useRecordContext,
} from 'react-admin';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useTitle } from '@src/hooks/useTitle';

import type { ShopifyProductRedirect } from './types';

const productUrl = (subdomain: string | null | undefined, handle: string | null | undefined) =>
  `https://${subdomain ?? ''}.myshopify.com/products/${handle ?? ''}`;

type RedirectWithGroup = ShopifyProductRedirect & {
  group?: {
    currentTarget: string | null | undefined;
    redirects: ShopifyProductRedirect[];
    count: number;
  };
};

const RedirectGroupVisualization = () => {
  const record = useRecordContext<RedirectWithGroup>();
  if (!record?.group || record.group.redirects.length === 0) return null;

  const { group } = record;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {'Redirect Group ('}
        {group.count}
        {' redirect'}
        {group.count === 1 ? '' : 's'}
        {')'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        All redirects converging on the same target handle.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {group.redirects.map((redirect: ShopifyProductRedirect) => (
          <Box
            key={redirect.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: redirect.id === record.id ? 'action.selected' : 'transparent',
            }}
          >
            <Chip
              label={redirect.fromHandle}
              component="a"
              href={productUrl(redirect.subdomain, redirect.fromHandle)}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              variant="outlined"
              size="small"
              sx={{ fontFamily: 'monospace' }}
            />
            <ArrowForwardIcon fontSize="small" color="action" />
            <Chip
              label={group.currentTarget}
              component="a"
              href={productUrl(record.subdomain, group.currentTarget)}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              color="primary"
              size="small"
              sx={{ fontFamily: 'monospace' }}
            />
            {redirect.status !== 'active' && (
              <Chip label={redirect.status ?? ''} size="small" color="warning" />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ShopifyRedirectsShow = () => {
  const { title } = useTitle('Shopify Redirect');

  return (
    <Show title={title}>
      <SimpleShowLayout>
        <TextField source="shopifyRedirectId" label="Shopify Redirect ID" />
        <FunctionField
          source="fromHandle"
          label="From Handle"
          render={(record: ShopifyProductRedirect) => (
            <a href={productUrl(record.subdomain, record.fromHandle)} target="_blank" rel="noopener noreferrer">
              {record.fromHandle}
            </a>
          )}
        />
        <TextField source="shopifyProductId" label="From Shopify Product ID" />
        <FunctionField
          source="toHandle"
          label="Original Target"
          render={(record: ShopifyProductRedirect) => (
            <a href={productUrl(record.subdomain, record.toHandle)} target="_blank" rel="noopener noreferrer">
              {record.toHandle}
            </a>
          )}
        />
        <FunctionField
          source="currentTarget"
          label="Resolved Target"
          render={(record: ShopifyProductRedirect) => (
            <a href={productUrl(record.subdomain, record.currentTarget)} target="_blank" rel="noopener noreferrer">
              {record.currentTarget}
            </a>
          )}
        />
        <FunctionField
          source="status"
          label="Status"
          render={(record: ShopifyProductRedirect) => (
            <Chip
              label={record.status ?? 'unknown'}
              color={record.status === 'active' ? 'success' : record.status === 'failed' ? 'warning' : 'default'}
              size="small"
            />
          )}
        />
        <FunctionField
          label="Catalog Item"
          render={(record: ShopifyProductRedirect) => {
            const item = record.catalogItem;
            if (!item) return '—';
            return `${item.title ?? ''} (${item.sku ?? `#${item.id}`})`;
          }}
        />
        <DateField source="createdAt" label="Created" showTime />
        <DateField source="updatedAt" label="Updated" showTime />
      </SimpleShowLayout>
      <Box sx={{ px: 2, pb: 2 }}>
        <RedirectGroupVisualization />
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            p: 2,
            mt: 3,
            borderRadius: 1,
            bgcolor: 'info.50',
            border: 1,
            borderColor: 'info.200',
          }}
        >
          <InfoOutlinedIcon fontSize="small" color="info" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Understanding Redirects
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
                <li>
                  The <strong>Original Target</strong> is where the product is
                  configured to be redirected to.
                </li>
                <li>
                  The <strong>Resolved Target</strong> is the final redirect
                  after resolving the redirect chain.
                </li>
              </ul>
              <strong>How redirect chain resolution works:</strong> When
              redirect chains are detected (A &rarr; B &rarr; C), Trove
              resolves them so each handle points directly to the final product
              (A &rarr; C, B &rarr; C).
            </Typography>
          </Box>
        </Box>
      </Box>
    </Show>
  );
};

export default ShopifyRedirectsShow;
